import "./TableWithAvatar.css";
import type { ReactNode } from "react";
import { useMemo, useSyncExternalStore } from "react";
import { AvatarGenerator } from "random-avatar-generator";
import { PlayerInfo, rankToCode, TeamId, toCardCode } from "../../interface/GameTypes";
import { PlayingCardFace } from "../PlayingCardFace";

const subscribeResize = (cb: () => void) => { window.addEventListener("resize", cb); return () => window.removeEventListener("resize", cb); };
const getIsCompact = () => window.innerWidth < 900 || (window.innerWidth < 1100 && window.innerHeight <= 520);
const getIsShortLandscapeWidePhone = () => window.innerWidth >= 900 && window.innerHeight <= 520;

const generator = new AvatarGenerator();

const SUIT_SYMBOLS: Record<string, string> = {
  Spades: "♠", Hearts: "♥", Diamonds: "♦", Clubs: "♣",
};
const SUIT_COLORS: Record<string, string> = {
  Spades: "#e0e0e0", Clubs: "#e0e0e0", Hearts: "#f44336", Diamonds: "#f44336",
};

export interface AvatarClickInfo {
  playerUUID: string;
  playerName: string;
  anchorX: number;
  anchorY: number;
}

interface GameTableProps {
  players: PlayerInfo[];
  mySeatIndex: number;
  dealerIndex: number;
  currentTurnIndex: number | null;
  currentRound?: Array<{ playerIndex: number; card: { suit: string; rank: string } }>;
  trumpSuit?: string | null;
  onAvatarClick?: (info: AvatarClickInfo) => void;
  /** Centered on green felt (e.g. opening shuffle). */
  tableCenterOverlay?: ReactNode;
}

export const GameTableWithPlayers = ({
  players,
  mySeatIndex,
  dealerIndex,
  currentTurnIndex,
  currentRound = [],
  trumpSuit,
  onAvatarClick,
  tableCenterOverlay,
}: GameTableProps) => {
  const compact = useSyncExternalStore(subscribeResize, getIsCompact);
  const shortLandscapeWidePhone = useSyncExternalStore(subscribeResize, getIsShortLandscapeWidePhone);
  const orderedPlayers = useMemo(
    () => [...players].sort((a, b) => a.seatIndex - b.seatIndex),
    [players]
  );
  const seatToPlayer = useMemo(() => {
    const m = new Map<number, PlayerInfo>();
    for (const p of orderedPlayers) m.set(p.seatIndex, p);
    return m;
  }, [orderedPlayers]);
  const count = orderedPlayers.length;
  const degStep = 360 / count;
  const myTablePos = Math.max(0, orderedPlayers.findIndex((p) => p.seatIndex === mySeatIndex));
  const rotationOffset = 180 - myTablePos * degStep;

  const radiusX = compact ? (shortLandscapeWidePhone ? 220 : 176) : 485;
  const radiusY = compact ? (shortLandscapeWidePhone ? 86 : 70) : 250;

  return (
    <div className="table-wrapper">
      {tableCenterOverlay}
      {orderedPlayers.map((player, tablePos) => {
        const seatIndex = player.seatIndex;
        const deg = tablePos * degStep + rotationOffset;
        const rad = (deg * Math.PI) / 180;
        const x = Math.sin(rad) * radiusX;
        const y = -Math.cos(rad) * radiusY;
        const isDealer = seatIndex === dealerIndex;
        const isCurrentTurn = seatIndex === currentTurnIndex;
        const teamClass = player.teamId === TeamId.A ? "team-a" : "team-b";
        const turnClass = isCurrentTurn ? "current-turn" : "";
        const avatarUrl = generator.generateRandomAvatar(player.uuid);
        const played = currentRound.find((pc) => pc.playerIndex === seatIndex);

        const isMe = seatIndex === mySeatIndex;
        const handleAvatarClick = (e: React.MouseEvent) => {
          if (isMe || !onAvatarClick) return;
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          onAvatarClick({
            playerUUID: player.uuid,
            playerName: player.name ?? `Seat ${seatIndex}`,
            anchorX: rect.left + rect.width / 2,
            anchorY: rect.top,
          });
        };

        return (
          <div
            key={player.uuid}
            className={`avatar-wrapper ${teamClass} ${turnClass}`}
            style={{
              transform: `translate(${x}px, ${y}px)`,
              cursor: isMe ? "default" : "pointer",
            }}
            onClick={handleAvatarClick}
          >
            <img
              className="avatar-img"
              src={avatarUrl}
              alt={player.name ?? "player"}
            />
            {isDealer && <span className="dealer-badge">D</span>}
            <span className="player-name">{player.name}</span>
            {played && (
              <span
                className="played-card-badge"
                style={{
                  color: played.card.suit === trumpSuit
                    ? "#FFD700"
                    : (SUIT_COLORS[played.card.suit] ?? "#fff"),
                  borderColor: played.card.suit === trumpSuit
                    ? "#FFD700"
                    : (SUIT_COLORS[played.card.suit] ?? "#fff"),
                  background: played.card.suit === trumpSuit
                    ? "rgba(255,215,0,0.18)"
                    : "rgba(20,30,50,0.88)",
                }}
              >
                {rankToCode[played.card.rank] ?? played.card.rank}
                {SUIT_SYMBOLS[played.card.suit] ?? played.card.suit}
              </span>
            )}
          </div>
        );
      })}

      {/* Played cards + names on separate rows so labels never overlap (fan only the faces). */}
      {currentRound.length > 0 && (
        <div className="round-cards-center">
          <div className="round-cards-fan">
            {currentRound.map((pc, idx) => {
              const code = toCardCode(pc.card);
              const isTrump = pc.card.suit === trumpSuit;
              const rotation = (idx - (currentRound.length - 1) / 2) * 6;
              return (
                <div
                  key={`card-${idx}`}
                  className={`table-card${isTrump ? " table-card--trump" : ""}`}
                  style={{ transform: `rotate(${rotation}deg)`, zIndex: idx }}
                >
                  <div className="table-card-face-wrap">
                    <PlayingCardFace
                      rankCode={code.value}
                      suitCode={code.suit}
                      widthPx={compact ? 52 : 68}
                      sx={isTrump ? { boxShadow: "0 0 10px rgba(255,215,0,0.45)" } : undefined}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="round-cards-names">
            {currentRound.map((pc, idx) => (
              <span key={`name-${idx}`} className="table-card-player-slot">
                {seatToPlayer.get(pc.playerIndex)?.name ?? `Seat ${pc.playerIndex}`}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Legacy export kept for backward compatibility (unused by new code)
export const TableWithAvatar = () => {
  return (
    <div className="table-wrapper">
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <img
          key={i}
          className={`avatar deg-${i * 45}`}
          src={generator.generateRandomAvatar()}
          alt={`player-${i}`}
        />
      ))}
    </div>
  );
};
